// Section 23: health and strength updates.
//   H' = clamp(H + 4[all needs met] - 12[hungry 2+] - 15[dehydrated 2+] - 15[exposed winter]
//              - 10[cold 2+] - 1.5[elder] - d_illness, 0, 100)
//   S' = clamp(S + 15 r_food + 5 r_water - 25(1 - r_food) - 15(1 - r_water) - 10[cold]
//              - 8[hard work] - 30[injured] + 15[rest], 20, cap)
// Every term is a modifier (needs, age, traits, illnesses, policies, jobs) so explain() shows it.
// Section 12 recovery: +4 when all needs are met, +8 with a healer visit (healthGain read with
// this season's care, phases/bodies/healing.ts); health below 40 caps strength at 60 (age
// contributor, D-29).
import { clamp } from '../../core/math';
import type { HealthLossCause, Person, PhaseContext } from '../../core/types';
import { seal } from '../shared/seal';
import { num } from '../shared/value';
import { careContext } from './healing';

const CAUSES: readonly HealthLossCause[] = [
  'hunger',
  'thirst',
  'exposure',
  'cold',
  'age',
  'illness',
];

// `p` is a draft: each property read goes through Immer's proxy, so read the id once (Section 2).
export function updateHealth(ctx: PhaseContext, p: Person): void {
  const id = p.id;
  const losses: Partial<Record<HealthLossCause, number>> = {};
  let total = 0;
  for (const cause of CAUSES) {
    const loss = Math.max(0, num(ctx, { kind: 'healthLoss', personId: id, cause }));
    if (loss > 0) {
      losses[cause] = loss;
      total += loss;
    }
  }
  const gain = Math.max(0, num(careContext(ctx), { kind: 'healthGain', personId: id }));
  p.health = clamp(p.health + gain - total, 0, ctx.cfg.people.statMax);
  if (total > 0) ctx.report.current.healthLoss[id] = seal(ctx, losses);
}

export function updateStrength(ctx: PhaseContext, p: Person): void {
  const id = p.id;
  const change = num(ctx, { kind: 'strengthChange', personId: id });
  const cap = num(ctx, { kind: 'strengthCap', personId: id });
  const floor = Math.min(ctx.cfg.bodies.strengthFloor, cap);
  p.strength = clamp(p.strength + change, floor, cap);
}
